// Shared constants & helpers for Travel Offers («عروض السفر»).
import { siteUrl } from "@/lib/site";
import { getFlags } from "@/lib/featureFlags";

export const OFFER_TYPES = [
  { id: "package", label: "بكج سفر" },
  { id: "hotel", label: "إقامة فندقية / منتجع" },
  { id: "cruise", label: "كروز" },
  { id: "tours", label: "جولات وتجارب" },
  { id: "mixed", label: "عرض مركّب" },
];

export const AUDIENCE = [
  { id: "families", label: "عائلات" },
  { id: "couples", label: "أزواج" },
  { id: "honeymoon", label: "شهر عسل" },
  { id: "friends", label: "أصدقاء" },
];

export const BASIS_LABELS = {
  perPerson: "للشخص",
  perCouple: "لشخصين",
  perFamily: "للعائلة",
  perUnit: "للوحدة",
  total: "إجمالي البكج",
};

export const FLIGHT_STATUS = {
  included: "الطيران مشمول",
  excluded: "الطيران غير مشمول",
  onRequest: "الطيران متاح بطلب تسعير إضافي",
};

export const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat("ar-BH", { day: "numeric", month: "long", year: "numeric" }).format(new Date(`${d}T12:00:00`));
  } catch {
    return d;
  }
};

const fmtNum = (n) => new Intl.NumberFormat("en").format(n);

export const priceLabel = (o, fromEnabled = false) => {
  if (!o) return "اطلب التسعير";
  if (o.askPrice) return "اطلب التسعير";
  const parts = [];
  if (o.priceBHD) parts.push(`${fmtNum(o.priceBHD)} د.ب`);
  if (o.priceSAR) parts.push(`${fmtNum(o.priceSAR)} ر.س`);
  if (!parts.length) return "اطلب التسعير";
  const basis = BASIS_LABELS[o.basis] || "";
  const travelers = o.travelersCount ? ` لـ${o.travelersCount} مسافرين` : "";
  return `${fromEnabled ? "ابتداءً من " : ""}${parts.join(" / ")} ${basis}${o.basis === "total" ? travelers : ""}`.trim();
};

export const nearestDateInfo = (o) => {
  const today = new Date().toISOString().slice(0, 10);
  if (o.travelMode === "fixed") {
    if (o.fixedFrom) return o.fixedTo && o.fixedTo !== o.fixedFrom ? `مغادرة ${fmtDate(o.fixedFrom)} — عودة ${fmtDate(o.fixedTo)}` : `مغادرة ${fmtDate(o.fixedFrom)}`;
    return "";
  }
  const periods = (o.periods || []).filter((p) => p.from);
  if (!periods.length) return "";
  const upcoming = periods.filter((p) => (p.to || p.from) >= today).sort((a, b) => a.from.localeCompare(b.from));
  const p = upcoming[0] || periods[periods.length - 1];
  return p.to ? `متاح من ${fmtDate(p.from)} إلى ${fmtDate(p.to)}` : `متاح من ${fmtDate(p.from)}`;
};

export const cardPrice = (o) => {
  const opts = (o.priceOptions || []).filter((x) => x.published !== false);
  const fp = o.fromPrice || {};
  if (fp.enabled && fp.optionId) {
    const opt = opts.find((x) => x.id === fp.optionId);
    if (opt && !opt.askPrice && (opt.priceBHD || opt.priceSAR)) return priceLabel(opt, true);
  }
  return opts.length ? priceLabel(opts[0]) : "اطلب التسعير";
};

const stayLines = (offer) => {
  const stays = offer.stays || [];
  if (!stays.length) return [];
  const parts = stays
    .map((s) => {
      const rooms = (s.roomTypes || []).map((r) => r.name).filter(Boolean).join("، ");
      // عروض متعددة الفئات: الفئة المختارة مذكورة في سطر «الخيار المختار» — لا نكرر القائمة كاملة
      const roomsTxt = rooms.length > 100 ? "فئات متعددة (المختارة في الخيار أدناه)" : rooms;
      return [s.hotelName, roomsTxt, s.mealPlan].filter(Boolean).join(" — ");
    })
    .filter(Boolean);
  return parts.length ? [`الإقامة: ${parts.join(" | ")}`] : [];
};

const transferLines = (offer) => {
  const t = (offer.transfers || [])[0];
  if (!t || !t.route) return [];
  return [`الانتقالات: ${[t.route, t.mode].filter(Boolean).join(" — ")}`];
};

// ملاحظة موحّدة تُعرض في كل عروض السفر (الحالية والمستقبلية) — من مكوّن/ثابت مشترك
export const OFFER_PRICE_DISCLAIMER =
  "الأسعار المعروضة استرشادية وقابلة للتغيير حسب تواريخ السفر، والعرض والطلب، والتوفر والعروض السارية وقت الطلب. قد لا يتوفر السعر المعروض عند الاستفسار نتيجة تحديث الأسعار أو نفاد التوفر. يؤكد المكتب السعر النهائي والتوفر قبل الحجز.";
export const OFFER_PRICE_DISCLAIMER_SHORT = "السعر قابل للتغيير ويخضع للتوفر";

// فلاتر العروض — القيم تُخزن في offer.filters وتُملأ من بيانات موثقة فقط
export const MEAL_FILTER_LABELS = {
  hb: "نصف إقامة", hb_plus: "نصف إقامة بلاس", fb: "وجبات كاملة", ai: "شامل كليًا", pai: "شامل كليًا مميز",
};
export const TRANSFER_FILTER_LABELS = {
  speedboat: "قارب سريع", seaplane: "طائرة مائية", domestic_speedboat: "نقل داخلي + قارب سريع",
};
export const BUDGET_RANGES = [
  { id: "u1000", label: "أقل من 1,000 د.ب", min: 0, max: 999 },
  { id: "1000-1500", label: "1,000 – 1,500 د.ب", min: 1000, max: 1500 },
  { id: "1500-2500", label: "1,500 – 2,500 د.ب", min: 1501, max: 2500 },
  { id: "2500p", label: "أكثر من 2,500 د.ب", min: 2501, max: Infinity },
];

// الخيارات السارية في بطاقات العروض (الخادم يرسل السارية فقط — هذا احتياط للمقارنة)
export const activeOpts = (offer) => {
  const today = new Date().toISOString().slice(0, 10);
  return (offer?.priceOptions || []).filter((o) => o.published !== false && (!o.periodTo || o.periodTo >= today));
};

export const offerNightsList = (offer) => [...new Set(activeOpts(offer).map((o) => o.nights).filter(Boolean))].sort((a, b) => a - b);

export const offerNightsLabel = (offer) => {
  const ns = offerNightsList(offer);
  if (!ns.length) return "";
  if (ns.length === 1) return `${ns[0]} ليالٍ`;
  return `${ns[0]}–${ns[ns.length - 1]} ليالٍ`;
};

export const buildOfferText = ({ offer, option, form }) => {
  const lines = [
    "طلب عرض سفر — دليل المسافر",
    "",
    `العرض: ${offer.title}`,
    `معرّف العرض: ${offer.id}`,
    `رابط العرض: ${siteUrl(`/offers/${offer.slug}`)}`,
    offer.type ? `النوع: ${(OFFER_TYPES.find((t) => t.id === offer.type) || {}).label || offer.type}` : "",
    (offer.countries || []).length ? `الوجهة: ${[...(offer.countries || []), ...(offer.cities || [])].join(" — ")}` : "",
    ...stayLines(offer),
    ...transferLines(offer),
    "",
    option ? `الخيار المختار: ${option.name} — ${priceLabel(option)}` : "الخيار: لم يُحدد (يحتاج تسعيرًا)",
    form.dateText ? `تاريخ السفر: ${form.dateText}` : "",
    `المسافرون: ${form.adults} بالغين${form.children ? ` + ${form.children} أطفال (أعمارهم: ${form.childAges || "—"})` : ""}`,
    form.rooms ? `عدد الوحدات (غرف/فلل/كبائن): ${form.rooms}` : "",
    form.departureAirport ? `مطار المغادرة: ${form.departureAirport}` : "",
    form.roomPref ? `تفضيل الغرفة/الكابينة: ${form.roomPref}` : "",
    form.notes ? `ملاحظات: ${form.notes}` : "",
    form.needsReprice ? "تنبيه: التاريخ/الأعداد تختلف عن شروط العرض المعلنة — يحتاج إعادة تسعير." : "",
    "",
    ...(getFlags().offersV2 ? ["أرغب في تأكيد السعر الحالي والتوفر لهذا العرض."] : []),
    "هذا طلب عرض وليس حجزًا مؤكدًا — السعر والتوفر يحتاجان تأكيد المكتب.",
  ];
  return lines.filter((l) => l !== "").join("\n");
};

export const buildOfferWhatsApp = ({ offer, option, form, whatsappBase }) => {
  const text = buildOfferText({ offer, option, form });
  const base = (whatsappBase || "https://wa.me/97313303332").split("?")[0];
  return `${base}?text=${encodeURIComponent(text)}`;
};
